import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '@/components/seo/SEOHead';
import { FileText } from 'lucide-react';

export const TermsPage = () => {
  const { t } = useTranslation('terms');

  return (
    <>
      <SEOHead
        title="Terms of Service - PeeTrack"
        description="Read the terms and conditions for using PeeTrack, including our medical disclaimer and user responsibilities."
        canonical="/terms"
      />

      <div className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-100 mb-4">
              <FileText className="h-8 w-8 text-cyan-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('title')}
            </h1>
            <p className="text-gray-600">{t('lastUpdated')}</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('intro.title')}
              </h2>
              <p className="text-gray-700">{t('intro.content')}</p>
            </section>

            {/* 1. Acceptance */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.acceptance.title')}
              </h2>
              <p className="text-gray-700">{t('sections.acceptance.content')}</p>
            </section>

            {/* 2. Medical Disclaimer */}
            <section className="mb-12 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <h2 className="text-2xl font-bold text-amber-900 mb-4">
                {t('sections.medicalDisclaimer.title')}
              </h2>
              <div className="mb-4 text-amber-900 font-semibold uppercase text-sm">
                {t('sections.medicalDisclaimer.important')}
              </div>
              <p className="text-amber-800 mb-4">{t('sections.medicalDisclaimer.content')}</p>
              <ul className="list-disc pl-6 text-amber-800 space-y-2">
                {(t('sections.medicalDisclaimer.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 3. Eligibility */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.eligibility.title')}
              </h2>
              <p className="text-gray-700">{t('sections.eligibility.content')}</p>
            </section>

            {/* 4. Account Responsibilities */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.account.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.account.intro')}</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                {(t('sections.account.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 5. Acceptable Use */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.usage.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.usage.intro')}</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                {(t('sections.usage.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 6. Your Data */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.data.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.data.ownership')}</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                {(t('sections.data.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 7. Intellectual Property */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.intellectual.title')}
              </h2>
              <p className="text-gray-700">{t('sections.intellectual.content')}</p>
            </section>

            {/* 8. Termination */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.termination.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.termination.intro')}</p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                {(t('sections.termination.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-700">{t('sections.termination.rights')}</p>
            </section>

            {/* 9. Disclaimer of Warranties */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.disclaimer.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.disclaimer.content')}</p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                {(t('sections.disclaimer.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-700">{t('sections.disclaimer.note')}</p>
            </section>

            {/* 10. Limitation of Liability */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.limitation.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.limitation.content')}</p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                {(t('sections.limitation.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-700">{t('sections.limitation.limit')}</p>
            </section>

            {/* 11. Indemnification */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.indemnification.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.indemnification.content')}</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                {(t('sections.indemnification.items', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* 12. Changes to Terms */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.changes.title')}
              </h2>
              <p className="text-gray-700">{t('sections.changes.content')}</p>
            </section>

            {/* 13. Governing Law */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.governing.title')}
              </h2>
              <p className="text-gray-700">{t('sections.governing.content')}</p>
            </section>

            {/* 14. Dispute Resolution */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.dispute.title')}
              </h2>
              <p className="text-gray-700">{t('sections.dispute.content')}</p>
            </section>

            {/* 15. Miscellaneous */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.miscellaneous.title')}
              </h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>Severability:</strong> {t('sections.miscellaneous.severability')}</p>
                <p><strong>Waiver:</strong> {t('sections.miscellaneous.waiver')}</p>
                <p><strong>Assignment:</strong> {t('sections.miscellaneous.assignment')}</p>
                <p><strong>Entire Agreement:</strong> {t('sections.miscellaneous.entire')}</p>
              </div>
            </section>

            {/* Contact */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('sections.contact.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('sections.contact.intro')}</p>
              <p className="text-gray-700 mb-2">{t('sections.contact.email')}</p>
              <p className="text-gray-700">{t('sections.contact.website')}</p>
            </section>

            {/* Back to Home */}
            <div className="text-center pt-8 border-t">
              <Link to="/" className="text-cyan-600 hover:text-cyan-700 font-semibold">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
